import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardFooter,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { useProgramContext } from '@/context/ProgramContext'
import {
	format,
	formatDistanceToNow,
	subDays,
	subMonths,
	subYears,
} from 'date-fns'
import { cn } from '@/lib/utils'
import {
	XAxis,
	YAxis,
	ResponsiveContainer,
	AreaChart,
	Area,
	Tooltip,
} from 'recharts'
import {
	Star,
	RotateCcw,
	AlertTriangle,
	CheckCircle,
	Mail,
	Twitter,
	Clock,
	Globe,
	Settings,
	ExternalLink,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { ReviewsPageSkeleton } from '@/components/reviews/ReviewsPageSkeleton'
import { motion } from 'framer-motion'
import { ReviewDetailView } from '@/components/reviews/ReviewDetailView'
import { extractTwitterUsername, getTwitterProfileImage } from '@/utils/reviews'
import {
	Review,
	TrendDataPoint,
	RatingDistribution,
} from '@/types/reviews.types'

import { Link } from 'react-router-dom'
import { useTranslation } from '@/hooks/useTranslation'

// Enhanced Review type (for matching image, actual data fetching might not support all)
interface ExtendedReview extends Review {
	website_reviewed_url?: string
	linkedin_name?: string
	instagram_handle?: string
	// Assuming publish_status might contain 'private' or similar for "Private Review" tag
	// Or add a specific field: is_private_review?: boolean;
}

const maskEmailSimple = (email?: string | null): string => {
	if (!email) return 'N/A'
	const parts = email.split('@')
	if (parts.length !== 2) return email // Not a valid email format, return as is
	const localPart = parts[0]
	const domain = parts[1]
	if (localPart.length <= 1) {
		// Handle very short local parts
		return `${localPart}***@${domain}`
	}
	return `${localPart.substring(0, 2)}***@${domain}`
}

// StarRating component (already present, ensure it matches target style if needed)
// For the target image, it's 4 filled, 1 outlined. Assuming score=4.
const StarRating = ({ score, t }: { score: number | null; t: (key: string) => string }) => {
	if (score === null)
		return (
			<span className="text-xs text-muted-foreground italic">{t('reviews.noRating')}</span>
		)
	return (
		<div className="flex items-center">
			{[1, 2, 3, 4, 5].map((star) => (
				<Star
					key={star}
					className={cn(
						'w-5 h-5', // Slightly larger to match image
						star <= score
							? 'text-yellow-400 fill-yellow-400'
							: 'text-gray-300 dark:text-gray-500' // Adjusted outline color
					)}
				/>
			))}
		</div>
	)
}

// Rating Distribution Chart Component
const RatingDistributionChart = ({
	data,
	total,
	t,
}: {
	data: RatingDistribution[]
	total: number
	t: (key: string) => string
}) => {
	return (
		<div className="space-y-3">
			{data.map((item) => (
				<div key={item.rating} className="flex items-center gap-3">
					<div className="w-8 text-sm font-medium">{item.rating}★</div>
					<div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
						<div
							className="h-full bg-primary rounded-full transition-all duration-500 ease-in-out"
							style={{
								width: `${total > 0 ? (item.count / total) * 100 : 0}%`,
							}}
						/>
					</div>
					<div className="w-8 text-sm text-muted-foreground">{item.count}</div>
				</div>
			))}
		</div>
	)
}

// Rating Trend Chart Component
const RatingTrendChart = ({ data, t }: { data: TrendDataPoint[]; t: (key: string) => string }) => {
	// If no data, show a message
	if (!data || data.length === 0) {
		return (
			<div className="h-[200px] flex items-center justify-center text-muted-foreground">
				<div className="text-center">
					<Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
					<p className="text-sm">{t('reviews.noRatingDataAvailable')}</p>
				</div>
			</div>
		)
	}

	// If only one data point, show it differently
	if (data.length === 1) {
		const singlePoint = data[0]
		return (
			<div className="h-[200px] flex items-center justify-center">
				<div className="text-center space-y-2">
					<div className="text-2xl font-bold text-primary">
						{singlePoint.rating}
					</div>
					<StarRating score={singlePoint.rating} t={t} />
					<p className="text-xs text-muted-foreground">
						{t('reviews.reviewCountOnDate', { count: singlePoint.count, date: singlePoint.date })}
					</p>
				</div>
			</div>
		)
	}

	return (
		<ResponsiveContainer width="100%" height={200}>
			<AreaChart
				data={data}
				margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
				<defs>
					<linearGradient id="colorRating" x1="0" y1="0" x2="0" y2="1">
						<stop
							offset="5%"
							stopColor="hsl(var(--primary))"
							stopOpacity={0.3}
						/>
						<stop
							offset="95%"
							stopColor="hsl(var(--primary))"
							stopOpacity={0.05}
						/>
					</linearGradient>
				</defs>
				<XAxis
					dataKey="date"
					tick={{ fontSize: 12 }}
					tickLine={false}
					axisLine={false}
					interval={data.length > 6 ? 'preserveStartEnd' : 0}
				/>
				<YAxis
					domain={[0, 5]}
					tick={{ fontSize: 12 }}
					tickCount={6}
					tickLine={false}
					axisLine={false}
				/>
				<Tooltip
					content={({ active, payload, label }) => {
						if (active && payload && payload.length) {
							return (
								<div className="bg-popover border rounded-lg shadow-lg p-3">
									<p className="font-medium">{label}</p>
									<p className="text-sm">{t('reviews.rating')}: {payload[0].value}</p>
									<p className="text-sm text-muted-foreground">
										{t('reviews.reviews')}: {payload[0].payload.count}
									</p>
								</div>
							)
						}
						return null
					}}
				/>
				<Area
					type="monotone"
					dataKey="rating"
					stroke="hsl(var(--primary))"
					strokeWidth={2}
					fill="url(#colorRating)"
					dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 4 }}
					activeDot={{ r: 6, stroke: 'hsl(var(--primary))', strokeWidth: 2 }}
				/>
			</AreaChart>
		</ResponsiveContainer>
	)
}

const Reviews = () => {
	const { t } = useTranslation()
	const { programId, currentProgram } = useProgramContext()
	const [searchParams, setSearchParams] = useSearchParams()
	const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null)

	// State
	const [searchQuery, setSearchQuery] = useState('')
	const [statusFilter, setStatusFilter] = useState<
		'all' | 'private' | 'approved' | 'rejected'
	>('all')
	const [timeframeFilter, setTimeframeFilter] = useState<
		'all' | 'week' | 'month' | 'quarter' | 'year'
	>('all')

	// Fetch reviews
	const {
		data: reviewsData = [], // Renamed to avoid conflict with component name
		isLoading,
		error: fetchError,
	} = useQuery<ExtendedReview[], Error>({
		// Use ExtendedReview
		queryKey: ['reviews', programId],
		queryFn: async () => {
			if (!programId) return []
			const { data, error } = await supabase
				.from('reviews')
				.select(
					'id, reporter_name, reporter_email, reporter_company, review_text, score, created_at, updated_at, approved_at, publish_status, widget_id, program_id, response_id, is_verified, email, name, social_media_link, consent_given, widget:widgets(id, name), program:programs(id, name), response:responses(id, user_metadata)'
				)
				.eq('program_id', programId)
				.order('created_at', { ascending: false })

			if (error) throw error

			const processedData = (data || []).map((item: any) => ({
				...item,
				// Placeholder data for new fields to match the target image
				website_reviewed_url: 'https://rocketwebsite.com', // Example
				linkedin_name: item.name || item.reporter_name || 'Tarun Sharma', // Example
				instagram_handle: item.reporter_name
					? `@${item.reporter_name.toLowerCase().replace(/\s+/g, '')}designs`
					: '@tarundesigns', // Example
				widget:
					item.widget && Array.isArray(item.widget)
						? item.widget[0]
						: item.widget || null,
				program:
					item.program && Array.isArray(item.program)
						? item.program[0]
						: item.program || null,
				response:
					item.response && Array.isArray(item.response)
						? item.response[0]
						: item.response || null,
			})) as ExtendedReview[]

			const reviewsWithImages = await Promise.all(
				processedData.map(async (currentReview) => {
					if (currentReview.social_media_link) {
						const username = extractTwitterUsername(
							currentReview.social_media_link
						)
						if (username) {
							return {
								...currentReview,
								twitterProfileImage: getTwitterProfileImage(username),
							}
						}
					}
					return currentReview
				})
			)
			return reviewsWithImages
		},
		enabled: !!programId,
	})

	// Filtered reviews and metrics
	const filteredReviews = useMemo(() => {
		if (!reviewsData) return []

		return reviewsData.filter((review) => {
			const matchesSearch =
				!searchQuery ||
				review.review_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				review.reporter_name
					?.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				review.reporter_email
					?.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				review.reporter_company
					?.toLowerCase()
					.includes(searchQuery.toLowerCase())

			const matchesStatus =
				statusFilter === 'all' || review.publish_status === statusFilter

			const reviewDate = new Date(review.created_at)
			let startDate = new Date()

			switch (timeframeFilter) {
				case 'week':
					startDate = subDays(new Date(), 7)
					break
				case 'month':
					startDate = subMonths(new Date(), 1)
					break
				case 'quarter':
					startDate = subMonths(new Date(), 3)
					break
				case 'year':
					startDate = subYears(new Date(), 1)
					break
				default:
					startDate = new Date(0) // All time
			}

			const matchesTimeframe = reviewDate >= startDate

			return matchesSearch && matchesStatus && matchesTimeframe
		})
	}, [reviewsData, searchQuery, statusFilter, timeframeFilter])

	// Calculate metrics
	const { averageRating, ratingDistribution, trendData } = useMemo(() => {
		if (!filteredReviews.length) {
			return {
				averageRating: 0,
				ratingDistribution: Array.from({ length: 5 }, (_, i) => ({
					rating: 5 - i,
					count: 0,
				})),
				trendData: [],
			}
		}

		// Average rating
		const validScores = filteredReviews
			.filter((r) => r.score !== null)
			.map((r) => r.score!)
		const avg =
			validScores.length > 0
				? parseFloat(
						(
							validScores.reduce((sum, score) => sum + score, 0) /
							validScores.length
						).toFixed(1)
				  )
				: 0

		// Rating distribution
		const distribution = Array.from({ length: 5 }, (_, i) => {
			const rating = 5 - i
			const count = filteredReviews.filter((r) => r.score === rating).length
			return { rating, count }
		})

		// Trend data - simplified approach using actual review dates
		const now = new Date()
		const startDate = subMonths(now, 3) // Show last 3 months

		// Get reviews from the last 3 months with valid scores
		const recentReviews = filteredReviews
			.filter((review) => {
				const reviewDate = new Date(review.created_at)
				return reviewDate >= startDate && review.score !== null
			})
			.sort(
				(a, b) =>
					new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
			)

		let trend: TrendDataPoint[] = []

		if (recentReviews.length === 0) {
			// No recent reviews
			trend = []
		} else if (recentReviews.length === 1) {
			// Single review - show just that point
			const review = recentReviews[0]
			trend = [
				{
					date: format(new Date(review.created_at), 'MMM d'),
					rating: review.score!,
					count: 1,
				},
			]
		} else if (recentReviews.length <= 5) {
			// Few reviews - show each review as a separate point
			trend = recentReviews.map((review) => ({
				date: format(new Date(review.created_at), 'MMM d'),
				rating: review.score!,
				count: 1,
			}))
		} else {
			// Many reviews - group by week using actual review dates
			const weeklyGroups: Record<
				string,
				{ reviews: typeof recentReviews; weekStart: Date }
			> = {}

			recentReviews.forEach((review) => {
				const reviewDate = new Date(review.created_at)
				// Get the Monday of the week for this review
				const monday = new Date(reviewDate)
				const dayOfWeek = reviewDate.getDay()
				const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
				monday.setDate(reviewDate.getDate() - daysToMonday)

				const weekKey = format(monday, 'yyyy-MM-dd')

				if (!weeklyGroups[weekKey]) {
					weeklyGroups[weekKey] = { reviews: [], weekStart: monday }
				}
				weeklyGroups[weekKey].reviews.push(review)
			})

			trend = Object.entries(weeklyGroups)
				.sort(([a], [b]) => a.localeCompare(b))
				.map(([weekKey, group]) => {
					const avgRating =
						group.reviews.reduce((sum, r) => sum + r.score!, 0) /
						group.reviews.length
					// Use the date of the first review in that week instead of Monday
					const firstReviewDate = group.reviews.sort(
						(a, b) =>
							new Date(a.created_at).getTime() -
							new Date(b.created_at).getTime()
					)[0].created_at

					return {
						date: format(new Date(firstReviewDate), 'MMM d'),
						rating: parseFloat(avgRating.toFixed(1)),
						count: group.reviews.length,
					}
				})
		}

		// If still no trend data but we have reviews, create a simple trend
		if (trend.length === 0 && filteredReviews.length > 0) {
			const validReviews = filteredReviews.filter((r) => r.score !== null)
			if (validReviews.length > 0) {
				// Show the most recent review
				const mostRecent = validReviews.sort(
					(a, b) =>
						new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				)[0]

				trend = [
					{
						date: format(new Date(mostRecent.created_at), 'MMM d'),
						rating: mostRecent.score!,
						count: validReviews.length,
					},
				]
			}
		}

		return {
			averageRating: avg,
			ratingDistribution: distribution,
			trendData: trend,
		}
	}, [filteredReviews])

	// Helper function to determine source
	const determineSource = (review: Review): 'App' | 'Website' | 'Email' => {
		if (review.source) return review.source

		const userMetadata = review.response?.user_metadata || {}
		if (userMetadata.isMobile) return 'App'
		if (userMetadata.isEmail) return 'Email'
		return 'Website'
	}

	// Function to get initials from name
	const getInitials = (name: string) => {
		if (!name) return '?'
		return name
			.split(' ')
			.map((part) => part[0])
			.join('')
			.toUpperCase()
	}

	useEffect(() => {
		const reviewIdFromUrl = searchParams.get('reviewId')
		if (reviewIdFromUrl) {
			setSelectedReviewId(reviewIdFromUrl)
		} else {
			setSelectedReviewId(null) // Clear selection if not in URL
		}
	}, [searchParams])

	const handleSelectReview = (reviewId: string) => {
		setSelectedReviewId(reviewId)
		setSearchParams({ reviewId: reviewId }, { replace: true })
	}

	const handleCloseDetailView = () => {
		setSelectedReviewId(null)
		setSearchParams({}, { replace: true })
	}

	// *** RENDER SKELETON ***
	if (isLoading) {
		return <ReviewsPageSkeleton />
	}

	// *** RENDER ERROR STATE ***
	if (fetchError) {
		return (
			<div className="p-4 md:p-8 max-w-7xl mx-auto">
				<Card>
					<CardContent className="p-6 text-center text-destructive">
						<AlertTriangle className="h-8 w-8 mx-auto mb-2" />
						{t('reviews.errorLoadingReviews')}: {fetchError.message}
					</CardContent>
				</Card>
			</div>
		)
	}

	return (
		<div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Star className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('reviews.reviews')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('reviews.manageAndAnalyzeReviews', { programName: currentProgram?.name || t('reviews.yourProgram') })}
						</p>
					</div>
				</div>
			</motion.div>

			{/* Public Review Page Section */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.1 }}>
				<Card>
					<CardContent className="p-4">
						{currentProgram?.public_reviews_enabled &&
						currentProgram?.public_reviews_slug ? (
							// Public page is enabled - show link
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
										<Globe className="h-5 w-5 text-green-600 dark:text-green-400" />
									</div>
									<div>
										<h3 className="font-medium text-foreground">
											{t('reviews.publicReviewPageActive')}
										</h3>
										<p className="text-sm text-muted-foreground">
											{t('reviews.reviewsPubliclyAccessible')}:
											<span className="font-mono text-xs ml-1 px-2 py-1 bg-muted rounded">
												{typeof window !== 'undefined' &&
													window.location.origin}
												/reviews/{currentProgram.public_reviews_slug}
											</span>
										</p>
									</div>
								</div>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											if (typeof window !== 'undefined') {
												window.open(
													`${window.location.origin}/reviews/${currentProgram.public_reviews_slug}`,
													'_blank'
												)
											}
										}}>
										<ExternalLink className="mr-2 h-4 w-4" />
										{t('reviews.viewPublicPage')}
									</Button>
									<Link to="/program-settings">
										<Button variant="ghost" size="sm">
											<Settings className="mr-2 h-4 w-4" />
											{t('reviews.settings')}
										</Button>
									</Link>
								</div>
							</div>
						) : (
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
										<Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
									</div>
									<div>
										<h3 className="font-medium text-foreground">
											{t('reviews.shareYourReviewsPublicly')}
										</h3>
										<p className="text-sm text-muted-foreground">
											{t('reviews.createPublicReviewPage')}
										</p>
									</div>
								</div>
								<Link to="/program-settings">
									<Button>
										<Settings className="mr-2 h-4 w-4" />
										{t('reviews.enablePublicPage')}
									</Button>
								</Link>
							</div>
						)}
					</CardContent>
				</Card>
			</motion.div>

			{/* Analytics Section */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{/* Average Rating */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base font-medium text-muted-foreground">
							{t('reviews.averageRating')}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							<div className="text-3xl font-bold">
								{averageRating.toFixed(1)}
								<span className="text-xl text-muted-foreground ml-1">/ 5</span>
							</div>
							<StarRating score={averageRating} t={t} />
							<p className="text-sm text-muted-foreground">
								{t('reviews.basedOnReviews', { count: filteredReviews.length })}
							</p>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-base font-medium text-muted-foreground">
							{t('reviews.ratingDistribution')}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<RatingDistributionChart
							data={ratingDistribution}
							total={filteredReviews.length}
							t={t}
						/>
					</CardContent>
				</Card>

				<Card className="lg:col-span-1">
					<CardHeader>
						<CardTitle className="text-base font-medium text-muted-foreground">
							{t('reviews.ratingTrend')}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<RatingTrendChart data={trendData} t={t} />
					</CardContent>
				</Card>
			</div>

			{/* Filters Section */}
			<Card className="p-4">
				<div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 w-full">
					<Input
						placeholder={t('reviews.searchNameEmailText')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="lg:col-span-3"
					/>
					<Select
						value={timeframeFilter}
						onValueChange={(
							value: 'all' | 'week' | 'month' | 'quarter' | 'year'
						) => setTimeframeFilter(value)}>
						<SelectTrigger>
							<SelectValue placeholder={t('reviews.timeframe')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('reviews.allTime')}</SelectItem>
							<SelectItem value="week">{t('reviews.lastWeek')}</SelectItem>
							<SelectItem value="month">{t('reviews.lastMonth')}</SelectItem>
							<SelectItem value="quarter">{t('reviews.lastQuarter')}</SelectItem>
							<SelectItem value="year">{t('reviews.lastYear')}</SelectItem>
						</SelectContent>
					</Select>
					<div className="lg:col-span-1 flex justify-end items-center">
						<Button
							variant="ghost"
							onClick={() => {
								setSearchQuery('')
								setStatusFilter('all')
								setTimeframeFilter('all')
							}}
							size="sm">
							<RotateCcw className="mr-2 h-4 w-4" /> {t('reviews.reset')}
						</Button>
					</div>
				</div>
			</Card>

			{/* Conditional rendering for ReviewDetailView - Replaced placeholder */}
			<ReviewDetailView
				reviewId={selectedReviewId}
				onClose={handleCloseDetailView}
				isOpen={!!selectedReviewId}
			/>

			{/* Reviews List */}
			{filteredReviews.length === 0 ? (
				<Card>
					<CardContent className="p-10 text-center text-muted-foreground">
						{t('reviews.noReviewsFoundMatchingCriteria')}
					</CardContent>
				</Card>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6 md:min-h-[264px]">
					{filteredReviews.map((review, index) => {
						// --- START OF NEW REVIEW CARD JSX ---
						const reviewerName =
							review.name || review.reporter_name || t('reviews.anonymous')
						const twitterUsername = review.social_media_link
							? extractTwitterUsername(review.social_media_link)
							: null

						return (
							<Card
								key={review.id}
								className="overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
								onClick={() => handleSelectReview(review.id)}>
								<CardHeader className="p-4">
									<div className="flex items-start justify-between gap-4">
										<div className="flex items-center gap-3">
											<div className="relative">
												<Avatar className="h-12 w-12 border">
													{(review as any).twitterProfileImage ? ( // Use 'any' for temp twitterProfileImage
														<AvatarImage
															src={(review as any).twitterProfileImage}
															alt={reviewerName}
														/>
													) : (
														<AvatarFallback>
															{getInitials(reviewerName)}
														</AvatarFallback>
													)}
												</Avatar>
											</div>
											<div className="flex items-center gap-2">
												<div>
													<p className="text-base font-semibold text-foreground">
														{reviewerName}
													</p>
													<p className="text-xs text-muted-foreground">
														{format(
															new Date(review.created_at),
															'MMMM d, yyyy'
														)}
													</p>
												</div>

												{review.is_verified && (
													<Badge
														variant="outline"
														className="text-xs border-green-500 text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700">
														<CheckCircle className="w-3 h-3 mr-1" /> {t('reviews.verified')}
													</Badge>
												)}
											</div>
										</div>
										<StarRating score={review.score} t={t} />
									</div>
								</CardHeader>

								<CardContent className="p-4 pt-0 space-y-3">
									<p className="text-sm text-foreground leading-relaxed mt-2 pb-2 border-b flex-1">
										{review.review_text || t('reviews.noReviewTextProvided')}
									</p>

									{/* Contact and Social Links */}
									<div className="space-y-1.5 text-sm">
										{(review.email || review.reporter_email) && (
											<div className="flex items-center gap-2 text-muted-foreground">
												<Mail className="h-4 w-4 flex-shrink-0" />
												<a href={`mailto:${review.email}`}>
													<span className="text-sm">{review.email}</span>
												</a>
											</div>
										)}

										{(twitterUsername ||
											review.linkedin_name ||
											review.instagram_handle) && (
											<div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-muted-foreground">
												{twitterUsername && (
													<a
														href={`https://twitter.com/${twitterUsername}`}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center gap-1 hover:text-blue-500"
														onClick={(e) => e.stopPropagation()}>
														<Twitter className="h-4 w-4" /> @{twitterUsername}
													</a>
												)}
											</div>
										)}
									</div>
								</CardContent>

								<CardFooter className="p-4 border-t flex items-center justify-between">
									<div className="mt-3 space-y-1">
										<div className="flex items-center text-xs text-muted-foreground">
											<Clock className="w-3.5 h-3.5 mr-1.5" />
											{t('reviews.submitted')}:{' '}
											{format(
												new Date(review.created_at),
												"MMM d, yyyy 'at' h:mm a"
											)}{' '}
											(
											{formatDistanceToNow(new Date(review.created_at), {
												addSuffix: true,
											})}
											)
										</div>
										{review.updated_at &&
											new Date(review.updated_at).getTime() !==
												new Date(review.created_at).getTime() && (
												<div className="flex items-center text-xs text-muted-foreground">
													<Clock className="w-3.5 h-3.5 mr-1.5" />
													{t('reviews.lastUpdated')}:{' '}
													{format(
														new Date(review.updated_at),
														"MMM d, yyyy 'at' h:mm a"
													)}{' '}
													(
													{formatDistanceToNow(new Date(review.updated_at), {
														addSuffix: true,
													})}
													)
												</div>
											)}
									</div>
								</CardFooter>
							</Card>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default Reviews
